import { PSlash } from "../widgets/Delimit";
import { MultiExpando } from "../widgets/MultiExpando";
import { Say } from "../widgets/Say";
import { Say2 } from "../widgets/Say2";
import './Prime.css';

/** expandable help that explains how to use the Prime area */
export function HelpPrime() {
  return (
    <div className="prime-help-div">
      <MultiExpando title="show help:"
        labels={['overview', 'typing IPA', 'phonemes', 'languages', 'synthesizer', 'IPA tips']}
        expanded={[false, false, false, false, false, false]}>
        {[
          <span key="overview" className="prime-help-body">
            <div className='prime-help-title'>Overview</div>
            <HelpOverview />
            <hr />
          </span>,
          <span key="typing IPA" className="prime-help-body">
            <div className='prime-help-title'>typing IPA</div>
            <HelpTypingIpa />
            <hr />
          </span>,
          <span key="phonemes" className="prime-help-body">
            <div className='prime-help-title'>Phonemes</div>
            <HelpPhonemes />
            <hr />
          </span>,
          <span key="languages" className="prime-help-body">
            <div className='prime-help-title'>Languages</div>
            <HelpLanguages />
            <hr />
          </span>,
          <span key="synth" className="prime-help-body">
            <div className='prime-help-title'>Synthesizer</div>
            <HelpSynth />
            <hr />
          </span>,
          <span key="ipa-tips" className="prime-help-body">
            <div className='prime-help-title'>IPA Tips</div>
            <HelpIpaTips />
            <hr />
          </span>,
        ]}
      </MultiExpando>
    </div>
  );
}

function HelpOverview() {
  return (<>
    <p>
      This page lets you type in and hear a variety of languages and language sounds.
    </p>
    <ul>
      <li>
        Use keyboard shortcuts or buttons to enter in IPA
        (the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>)
        - <b>show help: typing IPA</b> to learn more.
      </li>
      <li>
        Type or paste in <i>phonemes</i> (language sounds) using one of several different phoneme encodings
        - <b>show help: phonemes</b> to learn more.
      </li>
      <li>
        Type in various languages and hear them spoken with different accents
        - <b>show help: languages</b> to learn more.
      </li>
      <li>
        Tweak various settings of the speech synthesizer to change what it sounds like
        - <b>show help: synthesizer</b> to learn more.
      </li>
      <li>
        Get tips on how to effectively hear IPA spoken
        - <b>show help: IPA tips</b> to learn more.
      </li>
      <li>
        Learn more about <i>phonemes</i> and sound changing rules
        by following the <b>Phonetics</b> links on the right side of the page.
      </li>
      <li>
        Learn how to create a custom orthography (set of symbols) for your language
        combined with rules that convert it to phonemes so it can be spoken
        by following the <b><a href='?page=orthography'>Custom Orthography to IPA</a></b> link.
      </li>
      <li>
        Learn more about the challenges of pronouncing English
        by following the <b>Pronouncing English</b> links on the right side of the page.
      </li>
    </ul>
  </>);
}

function HelpTypingIpa() {
  return (<>
    <p>
      Many of the text boxes on this page (and several of the linked pages) let you type
      IPA (the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>),
      automatically converting some typed text into IPA and offering shortcuts for many other symbols.
    </p>
    <p>
      When the dropdown list next to <i>input:</i> is set to <i>IPA</i>, you can see the supported symbols by
      picking <i>show IPA keyboard</i> (to the right of <i>input: IPA</i> below).
      Hover the mouse over a symbol to see a description of it (or press-and-hold if using a touchscreen).
      You can either tap an IPA symbol to insert it into the <i>input</i> box
      or use the keyboard as described below:
    </p>
    <ul>
      <li>
        The first row shows the simple substitutions it supports.
        For example, if you type a single quote ('), it will replace it with a primary-stress symbol (ˈ).
      </li>
      <li>
        Most of the rows of the keyboard show substitutions it will do if you hold down the shift-key
        and tap the first letter or symbol one or more times.
        For example, if you hold down the <i>shift-key</i> and repeatedly hit the <i>A</i> key,
        it will cycle between IPA symbols similar to <i>a</i>: <b>ɑ</b>, <b>æ</b>, <b>ɐ</b>, and <b>ɒ</b>.
        The symbols in each shift-letter collection are generally ordered according to how common
        they are across the languages listed in <a href='https://phoible.org/'>PHOIBLE</a>.
        <br />
        Note that if you don't hold down the shift-key on your system to access the symbol
        (such as a colon (:) on a touchscreen keyboard),
        then when you type it, it will simply substitute in the first symbol in the list rather than cycling
        (such as the lengthen symbol (ː) when typing a colon (:)).
        Tap on any other symbol button to insert it.
      </li>
      <li>
        The last block contains a collection of symbols without a keyboard shortcut.
        Tap on a symbol to insert it into the <i>input</i> box.
      </li>
    </ul>
  </>);
}

function HelpPhonemes() {
  return (<>
    <p>
      You can enter in <i>phonemes</i> (language sounds) using
      IPA (the <a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>),
      &nbsp;<a href="https://en.wikipedia.org/wiki/Americanist_phonetic_notation">Americanist</a> notation,
      or CXS (<a href="https://en.wikibooks.org/wiki/Conlang/Appendix/CXS">Conlang X-SAMPA</a>).
      Pick the desired option from the <i>input</i> dropdown list to change the input type.
    </p>
    <p>
      When entering IPA, you have several additional options for adjusting your input and examining your IPA.
      These options appear below the <i>output</i> box (you may need to scroll down to see them).
      This includes seeing what was <i>spoken</i> by the speech synthesizer when it approximated a phoneme, <i>fixes</i> (replacing
      unknown symbols with recognized IPA or adding stress if there are no stress or tone markers),
      marking <i>diphthongs</i> to turn vowel pairs into diphthongs,
      creating <i>combos</i> of consonant pairs for affricates or co-articulated consonants,
      tweaking the <i>lengths</i> of vowels and diphthongs,
      seeing all the <i>rules</i> that have been applied to your IPA,
      and getting a listing of all the <i>features</i> for each IPA symbol
      (such as <i>bilabial</i>, <i>palatal</i>, <i>nasal</i>, <i>rounded</i>, etc.).
    </p>
    <p>
      <b>Spoken</b>&nbsp;
      See the IPA that the speech synthesizer used when it spoke.
      Since the synthesizer doesn't support every possible sound, this shows the approximation that was used when speaking.
    </p>
    <p>
      <b>Fixes:</b>&nbsp;
      This includes options for replacing unknown symbols or adding stress if needed.
      If it finds any unknown symbols, it offers an option for replacing them with recognized IPA.
      For example, IPA uses a special symbol (ː) to indicate that the preceding phoneme is longer in length.
      Since that symbol doesn't appear on a typical keyboard but looks very similar to a
      colon (:), sometimes your IPA may include the colon instead of the length symbol.
      It will list the unknown symbol with its Unicode value, giving you the option to type in symbol(s) to change it to.
      It will try to guess at a reasonable symbol (such as the length symbol instead of a colon)
      and will list the phonetic features of the replacement symbol if it's recognized IPA.
      Click the <i>change</i> button next to the replacement symbol once you've entered the proper symbol.
    </p>
    <p>
      The speech synthesizer sounds better if there are at least some stress (ˈ or ˌ) or tone (such as ˦) markers in your IPA.
      If it doesn't find any, it offers a few simple options for adding stress to each word.
      You can have it add primary stress to the first syllable (1), second syllable (2),
      second to last syllable (-2), or final syllable (-1).
      If a word includes syllable breaks (using periods), it will use those to figure out which syllable to stress.
      Otherwise, it will use the standard <a href="https://en.wikipedia.org/wiki/Sonority_hierarchy">sonority hierarchy</a> to
      infer where the syllables are.
    </p>
    <p>
      <b>Diphthongs:</b>&nbsp;
      This gives you the option of replacing a plain pair of vowels with an explicit dipthong (such as <PSlash>oʊ̯</PSlash> instead of <PSlash>oʊ</PSlash>).
      Marking diphthongs is especially important for the speech synthesize if you
      want to hear one syllable - <Say>/aɪ̯/</Say> - instead of two - <Say>/a.ɪ/</Say>.
    </p>
    <p>
      <b>Combos:</b>&nbsp;
      This gives you the option of tying together consonants in an affricate (such as <PSlash>t͡ʃ</PSlash> instead of <PSlash>tʃ</PSlash>)
      or co-articulated consonant (such as <PSlash>k͡p</PSlash> instead of <PSlash>kp</PSlash>).
      This difference is useful in cases such as <Say say="[ˈnətʃɛɫ]">nut-shell [ˈnətʃɛɫ]</Say> versus <Say say="[ˈnət͡ʃɛɫ]">nuchell [ˈnət͡ʃɛɫ]</Say>.
    </p>
    <p>
      <b>Length:</b>&nbsp;
      You can specify how long a vowel will last by typing in the vowel, its length (in milliseconds), and clicking <i>add</i>.
      The synthesizer supports values in the range of 50 to 580.
      The vowel can be a single symbol (e.g. <PSlash>a</PSlash>), diphthong (e.g. <PSlash>eɪ̯</PSlash>),
      rhotic (e.g. <PSlash>ɛɹ</PSlash>), length-modified vowel (e.g. <PSlash>aː</PSlash>), or syllabic consonant (e.g. <PSlash>n̩</PSlash>).
      You can change an existing value by typing in a new number and clicking the <i>change</i> button.
      Note that this is only approximate, so these numbers should be treated as <i>relative</i> values.
    </p>
    <p>
      <b>Rules:</b>&nbsp;
      Shows all the rules being applied to the input IPA
      including substituting symbols, adding stress markers, and tweaking vowel lengths.
      The <i>clear rules</i> button clears out all of the rules.
    </p>
    <p>
      <b>Features:</b>&nbsp;
      Provides a full listing of every symbol and compound symbol from the input.
      It first lists each symbol, breaking compound symbols into pieces, e.g., ones with diacritics or length symbols.
      Then it shows the <a href="https://en.wikipedia.org/wiki/Unicode">Unicode</a> value
      and phonetic features of the symbol.
    </p>
  </>);
}

function HelpLanguages() {
  return (<>
    <p>
      The <i>input</i> dropdown list contains a few options for input language
      - English, <a href="https://en.wikipedia.org/wiki/Old_English">Old English</a>, and Spanish -
      which convert the given language to IPA, typically through rules describing how to convert input symbols into IPA.
    </p>
    <p>
      English and Spanish also let you convert the generic pronunciation to a specific accent.
      For these languages, there will be an additional set of <i>accent</i> options below the <i>output</i> options.
      You can pick the accent you want then click <i>speak accent</i> to hear it spoken.
      Note that the <i>Americanized</i> Spanish accent attempts to speak Spanish with a General American (US English) accent.
    </p>
    <p>
      Since English spelling is so complex (see <a href='?page=pronounce-essay'>Pronouncing English is Hard</a>),
      it starts by looking up a word's pronunciation in a dictionary.
      If you check the <i>show issues</i> box, it will list any words that weren't in the dictionary.
      For these words, it has some rudimentary fallback rules it uses to attempt to pronounce it.
      Note that the <i>English (UK)</i> dictionary is fairly incomplete.
      It falls back to the <i>English (US)</i> dictionary when a word isn't present,
      which can affect accents that are inferred from the UK pronunciations.
      The various accents are generated by applying sound-changing rules to the dictionary pronunciations,
      so they should only be considered a rough approximation.
      See <a href='?page=accent-essay'>Making English Accents</a> for background on how English accents are generated.
    </p>
  </>);
}

function HelpSynth() {
  return (<>
    <p>
      There are several options for adjusting what the speech synthesizer sounds like, located in the upper right corner of this page.
      The <i>voice</i> and <i>pitch</i> options affect the quality of the voice,
      while the <i>speed</i> and <i>wordgap</i> options can make it easier (or harder) to hear individual sounds and words.
    </p>
    <ul>
      <li><b>voice:</b> Pick from several different voices that each have different qualities.</li>
      <li><b>pitch:</b> Make the voice higher or lower pitched by moving the slider to the right (higher) or left (lower)</li>
      <li><b>speed:</b> Move the slider to the left to slow down the speech or to the right to speed it up.</li>
      <li><b>volume:</b> Move the slider to the left to make the voice quieter or to the right to make it louder.</li>
      <li><b>wordgap:</b> Move the slider to the right to add a pause between words.</li>
    </ul>
    <p>
      Pick <i>test audio</i> to hear the current settings spoken.
      Pick <i>save last audio</i> to have the last synthesized speech saved to your download folder as an audio file.
    </p>
  </>);
}

function HelpIpaTips() {
  return (<>
    <p>
      Accurately pronouncing phonetic notation from a random source is a hard problem
      for a variety of reasons, which means the results should be considered approximate.
    </p>
    <ul>
      <li>
        There are many phonetic notations, such as the
        IPA (<a href="https://en.wikipedia.org/wiki/International_Phonetic_Alphabet">International Phonetic Alphabet</a>)
        and <a href="https://en.wikipedia.org/wiki/Americanist_phonetic_notation">Americanist</a> notation,
        which sometimes overlap, can be ambiguous, and have changed over the years.
      </li>
      <li>
        Sometimes a symbol is used for a phoneme that sounds different across variants of a language or language family.
        For example, some English dictionaries use <PSlash>ɔ</PSlash> for a phoneme that varies across accents,
        such as <i>cloth</i> sounding like <Say>[ˈkʰɫɔθ]</Say> or <Say>[ˈkʰɫɑθ]</Say>.
      </li>
      <li>
        Sometimes a symbol is used for a sound that has been inferred in a language that is no longer spoken.
        For example, the symbols h₁, h₂, and h₃ are sometimes used for reconstructed sounds
        in <a href="https://en.wikipedia.org/wiki/Proto-Indo-European_language">Proto-Indo-European</a>.
        While there are reasonable guesses for what they sounded like
        (maybe <Say2>h</Say2>, <Say2>χ</Say2> and <Say2>ɣ</Say2>?), we don't know for sure.
      </li>
      <li>
        Some special IPA symbols look very similar to non-IPA symbols, which means they can easily get mixed in.
        For example, the primary stress marker (IPA ˈ looks like a single quote '),
        the lengthen symbol (IPA ː looks like a colon :),
        and <PSlash>ñ</PSlash> might be one or two symbols
        (IPA combines the two symbols <i>n</i> and <i> ̃ </i>,
        while a Spanish keyboard uses ñ, a single symbol).
      </li>
    </ul >

    <p>
      <b>Listening to IPA</b>
    </p>
    <ul>
      <li>
        If there are any unrecognized symbols in the input, you can have the system change them to something it knows.
        Pick <i>show: fixes</i> from the options below <i>output</i> to be able to replace unknown symbols.
        For many symbols, it will show you a reasonable replacement so you can simply pick <i>change</i> to use it.
      </li>
      <li>
        By default, each vowel is pronounced separately, e.g., <Say speed={100}>/aɪ/</Say>.
        If you instead want it pronounced as a diphthong (or triphthong) where it slides from one vowel to the other,
        the second (and third) vowel should be followed by the <i>non-syllabic</i> symbol (&nbsp; ̯  ),
        e.g., <Say speed={100}>/aɪ̯/</Say>.
        Pick <i>show: diphthongs</i> from the options below <i>output</i> for an option to replace vowel pairs with explicit diphthongs.
        Alternately, you can explicitly mark them as being in different syllables by separating them
        with a period, e.g., <Say speed={100}>/a.ɪ/</Say>.
      </li>
      <li>
        Adjust the <i>Synthesizer Settings</i> shown in the upper right corner of the page.
        Slow down the speech synthesizer by moving the <i>speed</i> slider to the left.
        Add a pause between words by moving the <i>wordgap</i> slider to the right.
      </li>
      <li>
        The speech synthesizer sounds best if at least some words have stress or tone markers.
        If <i>show: fixes</i> is selected and no stress or tone is found in your IPA,
        it gives you an option to add stress to a selected syllable in every word.
      </li>
      <li>
        Pick <i>show: features</i> from the options below <i>output</i> to examine
        what the various IPA symbols mean to understand how they're being interpreted
      </li>
    </ul>

    <p>
      <b>Tweaking IPA</b>
    </p>
    <ul>
      <li>
        See <a href="?page=speech-essay">Picking Speech Phonemes</a> for details on IPA and
        what the speech synthesizer does with various symbols.
      </li>
      <li>
        If the <i>IPA keyboard</i> doesn't give you a quick keyboard shortcut for the IPA you want to enter,
        you could use an easy-to-type symbol such as @ (the <i>at</i> symbol) and use
        the <i>show fixes</i> feature to have the system change it to your desired IPA.
      </li>
      <li>
        If you need extra control over the lengths of various vowels and diphthongs,
        pick <i>show lengths</i> from the options below <i>output</i>.
        From here, you can enter in a vowel (<Say speed={100}>/a/</Say>), vowel with length modifier (<Say speed={100}>/aː/</Say>),
        diphthong (<Say speed={100}>/aɪ̯/</Say>), triphthong (<Say speed={100}>/ɛɪ̯ə̯/</Say>),
        rhotic combination (<Say speed={100}>/ɛɹ/</Say>), or syllabic consonant (<Say speed={100}>/n̩/</Say>).
        and type in the length of the phoneme in milliseconds.
        It supports a range from 50ms up to 580ms, though the the numbers should be considered as relative lengths
        (250 will make for a longer vowel than 150) rather than as exact values.
      </li>
    </ul>
  </>);
}

/*
function HelpEnglishTips() {
  return (<>
    <p>
      <b>Tweaking English</b>
    </p>
    <p>
      The pronunciation of some words varies based on how they're used.
      In <i>record</i> for example, the stress is on the first syllable if it's a noun or adjective
      - <Say say="I'll set the record.N in record.ADJ time">I'll set the record in record time</Say> -
      but it's on the second syllable if it's a verb
      - <Say say="I'm going to record.V the show">I'm going to record the show</Say>.
      If the system picks the wrong one, sometimes you can add the part of speech to
      the word to inform the system which version to choose, such as <Say>record.N</Say> versus <Say>record.V</Say>.
    </p>
    <p>
      Supported abbreviations:
    </p>
    <table><tbody>
      <tr><td>.ADJ</td><td>adjective</td></tr>
      <tr><td>.ADV</td><td>adverb</td></tr>
      <tr><td>.N</td><td>noun</td></tr>
      <tr><td>.PREP</td><td>preposition</td></tr>
      <tr><td>.V</td><td>verb</td></tr>
      <tr><td>.V.INTR</td><td>intransitive verb</td></tr>
      <tr><td>.V.PST</td><td>verb past tense</td></tr>
      <tr><td>.V.TR</td><td>transitive verb</td></tr>
    </tbody></table>

    <p>
      You can add <i>prosody markdown</i> (PMD) to English text to tweak how the word is spoken.
      For example, add an asterisk (*) at the start of word to add additional stress:
      <Say>this is the *last book</Say> versus <Say>this is the last *book</Say>.
    </p>
    <p>
      Supported PMD:
    </p>
    <table><tbody>
      <tr><td>*</td><td>add stress</td><td><Say>this is the last *book</Say></td></tr>
      <tr><td>%</td><td>remove all stress</td><td><Say>%what %did %you *do?</Say></td></tr>
      <tr><td>/</td><td>pass through IPA as-is</td><td><Say>specify /spəˈsɪfɪk pronunciation</Say></td></tr>
      <tr><td>1| 2| 3| 4| 5|</td><td>low, medium, or high tone</td><td><Say speed={80}>1|doe 2|ray 3|me 4|faw 5|sew</Say></td></tr>
      <tr><td>/| \|</td><td>rising ˩˥, falling ˥˩ tones</td><td><Say speed={80}> /|hey, \|hey</Say></td></tr>
      <tr><td>^| ~|</td><td>rise-fall ˨˦˨, fall-rise ˦˨˦ tones</td><td><Say speed={80}>^|oh, ~|oh</Say></td></tr>
    </tbody></table>
  </>);
}
*/
